import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 修复区域参数
 */
export interface YouchuanInpaintArea {
  /**
   * 区域宽度，范围500-4096
   */
  width: number;
  /**
   * 区域高度，范围500-4096
   */
  height: number;
  /**
   * 区域坐标点数组，至少包含6个坐标值（3个点）
   */
  points: number[];
}

/**
 * 遮罩参数，必须包含areas或url
 */
export interface YouchuanInpaintMask {
  /**
   * 区域数组
   */
  areas?: YouchuanInpaintArea[];
  /**
   * 遮罩图像URL
   */
  url?: string;
}

export interface YouchuanInpaintParams {
  /**
   * 任务ID
   */
  jobId: string;
  /**
   * 图像编号，必须是0、1、2或3
   * @values 0 | 1 | 2 | 3
   */
  imageNo: 0 | 1 | 2 | 3;
  /**
   * 重混提示词，长度必须在1-8192字符之间
   */
  remixPrompt: string;
  /**
   * 修复区域参数
   */
  area?: YouchuanInpaintArea;
  /**
   * 遮罩参数，必须包含areas或url
   */
  mask?: YouchuanInpaintMask;
  /**
   * 回调URL，必须是有效的HTTP/HTTPS URL
   */
  callback?: string;
}

export interface YouchuanInpaintResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_inpaint
 *
 * Youchuan Inpaint Image Generator
 *
 * Image inpainting implementation for Youchuan Open Platform (悠船开放平台).
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/api_inpaint
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanInpaint } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * // 使用遮罩URL
 * const result1 = await youchuanInpaint({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   remixPrompt: 'Fill with blue sky',
 *   mask: {
 *     url: 'https://example.com/mask.png'
 *   }
 * });
 *
 * // 使用区域坐标
 * const result2 = await youchuanInpaint({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   remixPrompt: 'Fill with green grass',
 *   area: {
 *     width: 1024,
 *     height: 768,
 *     points: [100, 100, 200, 100, 200, 200, 100, 200]
 *   }
 * });
 *
 * // 使用遮罩区域数组
 * const result3 = await youchuanInpaint({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   remixPrompt: 'Fill with sky',
 *   mask: {
 *     areas: [{
 *       width: 1024,
 *       height: 768,
 *       points: [100, 100, 200, 100, 200, 200]
 *     }]
 *   }
 * });
 *
 * console.log(result1); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanInpaintParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanInpaint(
  params: YouchuanInpaintParams,
): Promise<YouchuanInpaintResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_inpaint',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanInpaintResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
